import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SnoozeIcon from '@mui/icons-material/Snooze';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FunctionField, ReferenceField, TextField, Title, useNotify, usePermissions } from 'react-admin';
import { Permission } from '../../components';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { httpClientReactAdmin } from '../../lib';
import { formatCnpj } from '../../utils';

/**
 * Cobranças atrasadas por posto e por rede, para quem faz a cobrança. Depois de cobrar, ignora o
 * posto por alguns dias e ele some daqui até lá; o contador de ignoradas zera quando ele paga.
 */

const money = value => (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const day = iso => moment(iso).format('DD/MM/YYYY');
const daysLate = (iso, today) => moment(today).diff(moment(iso), 'days');
const newTab = { target: '_blank', rel: 'noopener noreferrer' };
const linkSx = { color: 'primary.main', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } };

/** Relatório de cobranças já filtrado no posto e em "Atrasado", nos meses do período. */
const invoicesUrl = (placeId, from, to) => `#/report-invoices?filter=${encodeURIComponent(JSON.stringify({
  placeId,
  status: ['delayed'],
  startMonth: moment(from).startOf('month').toISOString(),
  endMonth: moment(to).startOf('month').toISOString(),
}))}`;

const StatCard = ({ label, value, accent }) => (
  <Box sx={theme => ({
    p: { xs: 1.25, md: 2 }, borderRadius: 2, border: 1,
    borderColor: accent ? alpha(theme.palette.error.main, 0.4) : 'divider',
    bgcolor: accent ? alpha(theme.palette.error.main, 0.06) : 'background.paper',
  })}>
    <Typography variant="overline" color={accent ? 'error.main' : 'text.secondary'} sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' }, lineHeight: 1.6, display: 'block' }}>{label}</Typography>
    <Typography variant="h5" fontWeight={700} color={accent ? 'error.main' : 'text.primary'} sx={{ fontSize: { xs: '1rem', md: '1.5rem' } }}>{value}</Typography>
  </Box>
);

const IgnoreCountChip = ({ count }) => {
  if (!count) return <Typography color="text.disabled">—</Typography>;
  const color = count >= 3 ? 'error' : 'warning';
  return <Chip size="small" color={color} variant={count >= 3 ? 'filled' : 'outlined'} label={`${count}x`} />;
};

const IgnoreAction = ({ placeId, onIgnored }) => {
  const notify = useNotify();
  const [days, setDays] = useState('');
  const [saving, setSaving] = useState(false);
  const valid = Number.isInteger(Number(days)) && Number(days) >= 1 && Number(days) <= 365;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/overdue-bills/places/${placeId}/ignore`, {
        method: 'POST',
        body: JSON.stringify({ days: Number(days) }),
      });
      notify(`Ignorado até ${day(json.ignoredUntil)}`, { type: 'success' });
      onIgnored(placeId);
    } catch (error) {
      notify('Não foi possível ignorar o posto', { type: 'error' });
      setSaving(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
      <MuiTextField
        size="small"
        type="number"
        placeholder="dias"
        value={days}
        onChange={e => setDays(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && submit()}
        variant="outlined"
        fullWidth={false}
        inputProps={{ min: 1, max: 365, 'aria-label': 'Dias para ignorar' }}
        sx={{ width: 84, '& input': { py: 0.6 } }}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={saving ? <CircularProgress size={14} /> : <SnoozeIcon />}
        disabled={!valid || saving}
        onClick={submit}
      >
        Ignorar
      </Button>
    </Stack>
  );
};

const PlacesTable = ({ places, today, from, to, canIgnore, onIgnored }) => (
  <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Posto</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Rede</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Atrasadas</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Mais antiga</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Ignorado</TableCell>
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {places.map(place => (
          <TableRow key={place.placeId} hover>
            <TableCell>
              <Typography component="a" href={`#/stations/${place.placeId}/show`} {...newTab} variant="body2" sx={linkSx}>
                <ReferenceField record={place} source="placeId" reference="stations" link={false}>
                  <TextField source="name" />
                </ReferenceField>
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'nowrap' }}>
                <ReferenceField record={place} source="placeId" reference="stations" link={false}>
                  <FunctionField render={station => formatCnpj(station?.cnpj) || 'sem CNPJ'} />
                </ReferenceField>
              </Typography>
            </TableCell>
            <TableCell>
              <ReferenceField record={place} source="chainId" reference="chains" link={false} emptyText="—">
                <TextField source="name" />
              </ReferenceField>
            </TableCell>
            <TableCell align="center">
              <Chip size="small" color="error" label={place.overdueCount} />
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{money(place.overdueValue)}</TableCell>
            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
              {day(place.oldestDueDate)}
              <Typography variant="caption" color="error.main" component="div">
                {daysLate(place.oldestDueDate, today)} dias
              </Typography>
            </TableCell>
            <TableCell align="center"><IgnoreCountChip count={place.ignoreCount} /></TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Tooltip title="Ver cobranças atrasadas">
                  <IconButton size="small" component="a" href={invoicesUrl(place.placeId, from, to)} {...newTab}>
                    <ReceiptLongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {canIgnore && <IgnoreAction placeId={place.placeId} onIgnored={onIgnored} />}
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ChainsTable = ({ chains, today }) => (
  <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Rede</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Postos</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Atrasadas</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Mais antiga</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {chains.map(chain => (
          <TableRow key={chain.chainId || 'sem-rede'} hover>
            <TableCell>
              {chain.chainId ? (
                <Typography component="a" href={`#/chains/${chain.chainId}/show`} {...newTab} variant="body2" sx={linkSx}>
                  <ReferenceField record={chain} source="chainId" reference="chains" link={false}>
                    <TextField source="name" />
                  </ReferenceField>
                </Typography>
              ) : <Typography color="text.secondary">Sem rede</Typography>}
            </TableCell>
            <TableCell align="center">{chain.placesCount}</TableCell>
            <TableCell align="center"><Chip size="small" color="error" label={chain.overdueCount} /></TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{money(chain.overdueValue)}</TableCell>
            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
              {day(chain.oldestDueDate)}
              <Typography variant="caption" color="error.main" component="div">
                {daysLate(chain.oldestDueDate, today)} dias
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const groupByChain = places => Object.values(places.reduce((acc, place) => {
  const key = place.chainId || '';
  const chain = acc[key] || (acc[key] = { chainId: place.chainId, placesCount: 0, overdueCount: 0, overdueValue: 0, oldestDueDate: place.oldestDueDate });
  chain.placesCount += 1;
  chain.overdueCount += place.overdueCount;
  chain.overdueValue += place.overdueValue;
  if (place.oldestDueDate < chain.oldestDueDate) chain.oldestDueDate = place.oldestDueDate;
  return acc;
}, {})).sort((a, b) => b.overdueValue - a.overdueValue);

const ReportOverdueBills = () => {
  const { permissions } = usePermissions();
  const canIgnore = Boolean(permissions?.includes(PERMISSIONS.ADMIN));
  const [from, setFrom] = useState(() => moment().subtract(90, 'days').format('YYYY-MM-DD'));
  const [to, setTo] = useState(() => moment().format('YYYY-MM-DD'));
  const [state, setState] = useState({ loading: true, places: [], today: moment().format('YYYY-MM-DD') });
  const [tab, setTab] = useState('places');
  const notify = useNotify();

  const load = useCallback(async () => {
    if (!from || !to) return;
    setState(current => ({ ...current, loading: true }));
    try {
      const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/overdue-bills?from=${from}&to=${to}`);
      setState({ loading: false, places: json.places, today: json.today });
    } catch (error) {
      notify('Não foi possível carregar as cobranças atrasadas', { type: 'error' });
      setState(current => ({ ...current, loading: false }));
    }
    // notify é estável no react-admin; incluir causaria recarga em loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // A lista lê da réplica: tirar a linha na hora evita o posto ignorado "voltar" por atraso dela.
  const removePlace = placeId => setState(current => ({ ...current, places: current.places.filter(p => p.placeId !== placeId) }));

  const { places, today, loading } = state;
  const chains = useMemo(() => groupByChain(places), [places]);
  const totals = useMemo(() => places.reduce(
    (acc, p) => ({ count: acc.count + p.overdueCount, value: acc.value + p.overdueValue }),
    { count: 0, value: 0 }
  ), [places]);

  return (
    <Permission permission={PERMISSIONS.INVOICES_REPORT}>
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Title title="Cobranças Atrasadas" />

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <MuiTextField label="Vencimento de" type="date" size="small" fullWidth={false} value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: 160, md: 180 } }} />
          <MuiTextField label="até" type="date" size="small" fullWidth={false} value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: 160, md: 180 } }} />
          {loading && <CircularProgress size={22} />}
        </Stack>

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(3, 1fr)', mb: 2.5 }}>
          <StatCard label="Postos devendo" value={places.length} />
          <StatCard label="Cobranças atrasadas" value={totals.count} />
          <StatCard label="Valor em atraso" value={money(totals.value)} accent />
        </Box>

        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 1.5 }}>
          <Tab value="places" sx={{ textTransform: 'none', fontWeight: 600 }} label={`Por posto (${places.length})`} />
          <Tab value="chains" sx={{ textTransform: 'none', fontWeight: 600 }} label={`Por rede (${chains.length})`} />
        </Tabs>

        {!loading && places.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>Nenhuma cobrança atrasada no período.</Typography>
        ) : tab === 'places' ? (
          <PlacesTable places={places} today={today} from={from} to={to} canIgnore={canIgnore} onIgnored={removePlace} />
        ) : (
          <ChainsTable chains={chains} today={today} />
        )}
      </Box>
    </Permission>
  );
};

export default ReportOverdueBills;
